import React, { useContext } from 'react';
import {
  oneOf,
  bool as boolType,
  func,
  any,
  string as stringType,
  number as numberType,
} from 'prop-types';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import {
  bool,
  extend,
  string,
  shape,
  QueryProvider,
} from '@thd-nucleus/data-sources';
import { Carousel } from '@thd-olt-component-react/carousel';
import { ProductPod } from '@thd-olt-component-react/product-pod';
import { Col } from '@thd-olt-component-react/grid';
import { Image } from '@thd-olt-component-react/image';
import { LoadingPlaceholder } from './LoadingPlaceholder';
import { CarouselHeader } from './CarouselHeader';
import { RequiredAttributes } from './RequiredAttributes';
import '../../styles/thd-recs-containers.scss';

// the QueryProvider on line 54 doesnt get access to ProductPod because data?.products is empty at first
// this is what hoist() was originally intended for but the problem with hoist() is that it mutates
// components and runs into issues when they are in multiple places
// eslint-disable-next-line react/prop-types
const Wrapper = ({ children }) => children;
Wrapper.dataModel = extend({}, ProductPod);

const RecommendationsProductPodCarousel = (props) => {
  const { channel } = useContext(ExperienceContext);

  const isMobile = channel === 'mobile';

  const {
    anchorImage,
    data,
    loading,
    scheme,
    showLoading,
    storeId,

    // carouselProps,
    fixedItem,
    itemClass,
    itemWidthPixelsDesktop,
    itemWidthPixelsMobile,
    multiItem,
    onClickItem,
    showArrows,

    // productPodProps,
    brandTitleMaxLine,
    hideATC,
    hideFavorites,
    hideFulfillment,
    hideLimitPerOrder,
    hidePriceBadge,
    hideRating,
    hideRebates,
    hideSavingsText,
    hideShippingInfo,
    hideShopThisCollection,
    loadingImageHeight,
    loadingLinesCount,
    zipCode,
    isRequiredAttributes
  } = props;

  const defaultVariables = {
    storeId,
    zipCode,
    skipSpecificationGroup: true,
    skipKPF: true,
    skipInstallServices: true,
    installationServices: false
  };

  const { products, metadata } = data || {};
  let title = '';
  if (isRequiredAttributes) {
    title = metadata?.messaging;
  } else {
    title = metadata?.title ? metadata.title : metadata?.batteryName;
  }

  return (
    <Wrapper>
      {showLoading && loading && (
        <LoadingPlaceholder
          scheme={scheme}
          itemClass={itemClass}
          fixedItem={fixedItem}
          multiItem={multiItem}
          itemWidthPixelsDesktop={itemWidthPixelsDesktop}
          itemWidthPixelsMobile={itemWidthPixelsMobile}
          loadingLinesCount={loadingLinesCount}
          loadingImageHeight={loadingImageHeight}
        />
      )}
      {products?.length > 0 && (
        <>
          <CarouselHeader title={title} />
          {
            isRequiredAttributes
            && <RequiredAttributes metadata={metadata} />
          }
          {anchorImage && !isMobile && (
            <Col xs="4" className="visually-similar--centered">
              <Image
                src={anchorImage}
                alt="main-product-image"
                height="400"
                width="400"
                data-testid="anchor-image"
              />
              <div className="visually-similar__currentItem__label">Current Item</div>
            </Col>
          )}
          {metadata?.batteryImageUrl && (
            <Col xs="4" className="battery-systems--feature">
              <Image
                src={metadata?.batteryImageUrl}
                alt="main-product-image"
                height="300"
                width="300"
                data-testid="anchor-image"
              />
              <div className="battery-systems__feature--title">{metadata?.batteryTitle}</div>
              <div className="battery-systems__feature--list">
                {/* eslint-disable-next-line react/no-danger */}
                <ul dangerouslySetInnerHTML={{ __html: metadata?.batteryDesc }} />
              </div>
              <div className="battery-systems__shop--link">
                <a
                  href={metadata?.batteryPlpUrl}
                  rel="noreferrer"
                  target={isMobile ? '_self' : '_blank'}
                  data-testid="tooltip-product"
                > Shop {metadata?.batteryName}
                </a>
              </div>
            </Col>
          )}

          <Col xs={metadata?.batteryImageUrl || anchorImage ? '8' : '12'}>
            <QueryProvider
              dataSource="dynamicRecs"
              cacheKey={`${scheme}-recommendations-carousel`}
              defaultVariables={defaultVariables}
            >
              <Carousel
                name={scheme}
                id={scheme}
                itemClass={itemClass}
                fixedItem={fixedItem}
                multiItem={multiItem}
                itemWidthPixelsDesktop={itemWidthPixelsDesktop}
                itemWidthPixelsMobile={itemWidthPixelsMobile}
                showArrows={showArrows}
                totalItems={data?.products.length}
                onClickItem={onClickItem}
              >

                {data?.products.map((dynamicProduct, idx) => {
                  const { product } = dynamicProduct;
                  const { itemId } = product;

                  return (
                    <ProductPod
                      brandTitleMaxLine={brandTitleMaxLine}
                      key={itemId}
                      itemId={itemId}
                      hideATC={hideATC}
                      hideFavorites={hideFavorites}
                      hideFulfillment={hideFulfillment}
                      hideLimitPerOrder={hideLimitPerOrder}
                      hidePriceBadge={hidePriceBadge}
                      hideRating={hideRating}
                      hideRebates={hideRebates}
                      hideSavingsText={hideSavingsText}
                      hideShopThisCollection={hideShopThisCollection}
                      hideShippingInfo={hideShippingInfo}
                      storeId={storeId}
                      position={idx}
                    />
                  );
                }
                )}
              </Carousel>
            </QueryProvider>
          </Col>
        </>
      )}
    </Wrapper>
  );
};

const productModel = extend({}, {
  itemId: string(),
  dataSource: string(),
}, ProductPod.dataModel.product);

export const wrappedProduct = shape({
  category: string(),
  description: string(),
  idEndDate: string(),
  idPromoDetails: string(),
  idStartDate: string(),
  isAnchor: bool(),
  product: shape(productModel),
  // itemId: string(),
  slot: shape({
    description: string(),
    isRequired: bool(),
    name: string(),
  })
});

RecommendationsProductPodCarousel.dataModel = extend(Wrapper, wrappedProduct);

RecommendationsProductPodCarousel.displayName = 'RecommendationsProductPodCarousel';

RecommendationsProductPodCarousel.propTypes = {
  ...Carousel.propTypes,
  ...ProductPod.propTypes,
  // eslint-disable-next-line react/forbid-prop-types
  data: any,
  loading: boolType,
  scheme: stringType,
  showLoading: boolType,
  storeId: stringType.isRequired,

  // carouselProps
  fixedItem: boolType,
  multiItem: boolType,
  itemClass: stringType,
  itemWidthPixelsDesktop: numberType,
  itemWidthPixelsMobile: numberType,
  onClickItem: func, // requires ( index: number, item: any ),

  // productPodProps
  // product-pod take only 2 values for brandTitleMaxLine prop
  brandTitleMaxLine: oneOf([2, 3, null]),
  hideATC: boolType,
  hideFavorites: boolType,
  hideFulfillment: boolType,
  hideLimitPerOrder: boolType,
  hidePriceBadge: boolType,
  hideRating: boolType,
  hideRebates: boolType,
  hideSavingsText: boolType,
  hideShopThisCollection: boolType,
  hideShippingInfo: boolType,
  loadingImageHeight: numberType,
  loadingLinesCount: numberType,
};

RecommendationsProductPodCarousel.defaultProps = {
  scheme: 'drecs',
  showLoading: false,
  loading: false,
  data: null,

  fixedItem: true,
  itemClass: '',
  multiItem: false,
  itemWidthPixelsDesktop: 220,
  itemWidthPixelsMobile: 150,
  onClickItem: () => {},

  brandTitleMaxLine: null,
  hideATC: false,
  hideFavorites: true,
  hideFulfillment: true,
  hideLimitPerOrder: true,
  hidePriceBadge: true,
  hideRating: false,
  hideRebates: true,
  hideSavingsText: true,
  hideShopThisCollection: true,
  hideShippingInfo: true,
  loadingImageHeight: null,
  loadingLinesCount: 5
};

export { RecommendationsProductPodCarousel };
